import React, { useEffect, useRef } from "react";
import "./CustomEditor.css";
import {
  TypeIcon,
  BoldIcon,
  ItalicIcon,
  UnderlineIcon,
  StrikeIcon,
  AlignLeftIcon,
  AlignCenterIcon,
  AlignRightIcon,
  AlignJustIcon,
  ListOlIcon,
  ListUlIcon,
  LinkIcon,
  ImageIcon,
  CodeIcon,
} from "../rich-text-editor/EditorIcons";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";

interface CustomEditorProps {
  value: string;
  onChange: (html: string) => void;
  file?: string;
}

const CustomEditor: React.FC<CustomEditorProps> = ({
  value,
  onChange,
  file = "faq",
}) => {
  const editorRef = useRef<HTMLDivElement>(null);

  const handleCommand = (
    command: string,
    value: string | undefined = undefined
  ) => {
    document.execCommand(command, false, value);
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const handleInsertLink = () => {
    let url = prompt("Enter the URL");
    if (url) {
      if (!/^https?:\/\//i.test(url)) {
        url = "https://" + url;
      }
      const selection = window.getSelection();
      if (!selection || selection.toString().trim() === "") {
        alert("Please select text to hyperlink first.");
        return;
      }
      handleCommand("createLink", url);
    }
  };

  const handleInsertImage = () => {
    let url = prompt("Enter the Image URL");
    if (url) {
      if (!/^https?:\/\//i.test(url)) {
        url = "https://" + url;
      }

      const imgHTML = `<img src="${url}" alt="${url}" style="max-width:100%; height:auto;" />`;
      document.execCommand("insertHTML", false, imgHTML);

      if (editorRef.current) {
        onChange(editorRef.current.innerHTML);
      }
    }
  };

  // Click-to-edit image width
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    const handleImageClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === "IMG") {
        const currentWidth = (target as HTMLImageElement).width.toString();
        const newWidth = prompt(
          "Enter image width in pixels:",
          currentWidth || "300"
        );
        if (newWidth) {
          (target as HTMLImageElement).setAttribute("width", newWidth);
          onChange(editor.innerHTML);
        }
      }
    };

    editor.addEventListener("click", handleImageClick);
    return () => {
      editor.removeEventListener("click", handleImageClick);
    };
  }, [onChange]);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    const handlePaste = (e: ClipboardEvent) => {
      e.preventDefault();
      const text = e.clipboardData?.getData("text/plain") || "";

      const lines = text
        .split("\n")
        .map((line) => line.trim())
        .filter((line) => line.length > 0);

      let formatted = "";

      if (lines.length > 1) {
        formatted =
          "<ul>" +
          lines
            .map((line) => line.replace(/^([•⦁\-\*]\s*)/, ""))
            .map((cleanLine) => `<li>${cleanLine}</li>`)
            .join("") +
          "</ul>";
      } else {
        formatted = lines[0];
      }

      document.execCommand("insertHTML", false, formatted);

      if (editorRef.current) {
        onChange(editorRef.current.innerHTML);
      }
    };

    editor.addEventListener("paste", handlePaste);
    return () => {
      editor.removeEventListener("paste", handlePaste);
    };
  }, [onChange]);

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value;
    }
  }, [value]);

  return (
    <>
      {file === "faq" ? (
        <div className="custom-editor">
          <div className="toolbar">
            <button
              type="button"
              onClick={() => handleCommand("formatBlock", "<p>")}
            >
              <TypeIcon />
            </button>
            <select
              onChange={(e) => handleCommand("formatBlock", e.target.value)}
            >
              <option value="<p>">Normal</option>
              <option value="<h1>">H1</option>
              <option value="<h2>">H2</option>
              <option value="<h3>">H3</option>
              <option value="<h4>">H4</option>
              <option value="<h5>">H5</option>
              <option value="<h6>">H6</option>
            </select>

            <select
              onChange={(e) => handleCommand("fontSize", e.target.value)}
              defaultValue="3"
            >
              <option value="1">10px</option>
              <option value="2">13px</option>
              <option value="3">16px</option>
              <option value="4">18px</option>
              <option value="5">24px</option>
              <option value="6">32px</option>
              <option value="7">48px</option>
            </select>
            <button type="button" onClick={() => handleCommand("bold")}>
              <BoldIcon />
            </button>
            <button type="button" onClick={() => handleCommand("italic")}>
              <ItalicIcon />
            </button>
            <button type="button" onClick={() => handleCommand("underline")}>
              <UnderlineIcon />
            </button>
            <button
              type="button"
              onClick={() => handleCommand("strikeThrough")}
            >
              <StrikeIcon />
            </button>
            <button type="button" onClick={() => handleCommand("justifyLeft")}>
              <AlignLeftIcon />
            </button>
            <button
              type="button"
              onClick={() => handleCommand("justifyCenter")}
            >
              <AlignCenterIcon />
            </button>
            <button type="button" onClick={() => handleCommand("justifyRight")}>
              <AlignRightIcon />
            </button>
            <button type="button" onClick={() => handleCommand("justifyFull")}>
              <AlignJustIcon />
            </button>
            <button
              type="button"
              onClick={() => handleCommand("insertOrderedList")}
            >
              <ListOlIcon />
            </button>
            <button
              type="button"
              onClick={() => handleCommand("insertUnorderedList")}
            >
              <ListUlIcon />
            </button>
            <button type="button" onClick={handleInsertLink}>
              <LinkIcon />
            </button>
            <button type="button" onClick={handleInsertImage}>
              <ImageIcon />
            </button>
            <button
              type="button"
              onClick={() => handleCommand("formatBlock", "<pre>")}
            >
              <CodeIcon />
            </button>
          </div>

          <div
            ref={editorRef}
            className="editor-area"
            contentEditable
            suppressContentEditableWarning
            onInput={(e) => onChange((e.target as HTMLDivElement).innerHTML)}
          />
        </div>
      ) : (
        <div className="custom-editor">
          <CKEditor
            editor={ClassicEditor as any}
            data={value}
            config={{
              toolbar: [
                "heading",
                "|",
                "bold",
                "italic",
                "mediaEmbed",
                "bulletedList",
                "numberedList",
                "insertTable",
                "codeBlock",
                "undo",
                "redo",
              ],
              image: {
                toolbar: [
                  "imageTextAlternative",
                  "toggleImageCaption",
                  "imageStyle:alignLeft",
                  "imageStyle:alignCenter",
                  "imageStyle:alignRight",
                  "insertImageViaUrl",
                ],
              },
            }}
            onChange={(_, editor) => onChange(editor.getData())}
          />
        </div>
      )}
    </>
  );
};

export default CustomEditor;
